/**
 * DownloaderSection Component
 * 
 * A reusable downloader section that can be customized for different platforms.
 * Implements Clean Architecture with separation of concerns and DRY principles.
 * 
 * Features:
 * - Platform-specific configuration
 * - Reusable download logic
 * - Consistent UI across all platform pages
 * - Error handling and validation
 * - Accessibility support
 * - i18n ready
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { 
  Download as DownloadIcon, 
  CheckCircle as CheckCircleIcon,
  Info as InformationCircleIcon
} from 'lucide-react';
import { GoogleAd } from '../ads/GoogleAd';
import debugLogger from '@/lib/utils/debugLogger';
import secureStorage from '@/utils/secureStorage';


import { useVideoInfo } from '@/lib/hooks';
import type { VideoFormat } from '@/lib/hooks/useVideoInfo';
import VideoInfoDisplay from '@/components/common/VideoInfoDisplay';
import ErrorBoxSection from '@/components/common/ErrorBoxSection';
import { Button } from '@/components/ui/button';
import type { PlatformId } from '../../config/platforms';
import { detectPlatformFromUrl } from '@/lib/utils/platformUtils';

interface DownloaderSectionProps {
  defaultPlatform?: PlatformId;
  title: string;
  description: string;
  placeholderText: string;
  features?: string[];
  className?: string;
}

const DownloaderSection: React.FC<DownloaderSectionProps> = ({
  defaultPlatform = "dailymotion",
  title,
  description,
  placeholderText,
  features = [],
  className = ''
}) => {
  const { t } = useTranslation('common');
  
  // State management
  const [url, setUrl] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState<boolean | null>(null);
  const [downloadingFormat, setDownloadingFormat] = useState<VideoFormat | null>(null);
  
  // Refs for auto-scroll
  const videoInfoRef = useRef<HTMLDivElement>(null);
  
  // API hooks
  const { loading: isLoading, error: apiError, videoInfo, fetchVideoInfo } = useVideoInfo();

  // Animation effect when component mounts
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Reset result/error when user changes input
  useEffect(() => {
    setIsSubmitted(false);
    setIsValidUrl(null);
  }, [url]);

  // Auto-scroll to video info when it's loaded
  useEffect(() => {
    if (videoInfo && videoInfoRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        videoInfoRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [videoInfo]);


  // URL validation
  const validateUrl = (str: string): boolean => {
    try {
      const u = new URL(str);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  // Platform detection from URL using util
  const getPlatformFromUrl = (url: string): PlatformId => {
    const detected = detectPlatformFromUrl(url);
    if (detected && detected.id) {
      return detected.id as PlatformId;
    }
    return defaultPlatform as PlatformId;
  };

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    const isValid = validateUrl(url);
    setIsValidUrl(isValid);
    setIsSubmitted(true);
    
    if (isValid) {
      try {
        const platform = getPlatformFromUrl(url);
        await fetchVideoInfo({ url: url.trim(), platform });
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        debugLogger.error('Error fetching video info:', { error: errorMessage });
      }
    }
  };

  // Download handler with progress page
  const handleDownload = async (format: VideoFormat) => {
    if (!videoInfo) return;

    try {
      setDownloadingFormat(format);

      // Prepare download data
      const downloadData = {
        videoInfo: {
          ...videoInfo,
          originalUrl: url // Add original URL for platform detection
        },
        selectedFormat: format,
        sessionToken: (videoInfo as any).sessionToken || 'session_' + Date.now(),
        timestamp: Date.now()
      };

      // Store data securely and get session token
      const sessionToken = secureStorage.storeSecurely('download_data', downloadData);
      
      // Open progress page with secure token
      const progressUrl = `/download-progress?token=${sessionToken}`;
      window.open(progressUrl, '_blank');



      setTimeout(() => {
        setDownloadingFormat(null);
      }, 1000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      debugLogger.error('Failed to open download progress page', { error: errorMessage, url });
      setDownloadingFormat(null);
    }
  };

  // Error message helper
  const getErrorMessage = (error: any): string => {
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object' && 'message' in error) return error.message;
    return 'An unknown error occurred';
  };

  return (
    <section className={`w-full py-16 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="w-full max-w-4xl mx-auto">
          {/* Title and Description */}
          <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold gradient-text mb-4">
              {title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {description}
            </p>
          </div>

          {/* Download Form */}
          <div className={`w-full max-w-2xl mx-auto mb-8 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row sm:items-stretch gap-3">
              <div className="flex-1 relative">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={placeholderText}
                  className="w-full h-14 px-6 py-4 rounded-xl border border-input bg-white dark:bg-gray-800 text-foreground shadow-sm focus-ring"
                  required
                  autoFocus
                  aria-label={placeholderText}
                />
                {url && (
                  <button 
                    type="button" 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    onClick={() => setUrl("")}
                    aria-label="Clear input"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
              <Button
                type="submit"
                size="lg"
                className="button-hover bg-primary hover:bg-primary/90 text-white font-medium px-8 py-4 rounded-xl shadow-md h-14"
                disabled={isLoading}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('processing')}
                  </>
                ) : (
                  <>
                    <DownloadIcon className="w-5 h-5 mr-2" />
                    {t('videoDownloader.fetchInfo')}
                  </>
                )}
              </Button>
            </form>
            {/* Google AdSense: Responsive ad below input */}
            <div className="w-full">
              <GoogleAd 
                adSlot="2762627887"
                className="mx-auto max-w-4xl"
              />
            </div>
          </div>

          {/* Results Section */}
          {isSubmitted && isValidUrl === true && videoInfo && (
            <div ref={videoInfoRef} className="mt-6">
              <VideoInfoDisplay
                title={videoInfo.title}
                thumbnail={videoInfo.thumbnail}
                duration={videoInfo.duration}
                formats={videoInfo.formats}
                selectedFormat={null}
                onSelectFormat={() => {}}
                onDownload={handleDownload}
                disabled={!!downloadingFormat}
                downloadLoading={!!downloadingFormat}
              />
            </div>
          )}

          {/* Loading State */}
          {isSubmitted && isValidUrl === true && isLoading && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg">
              <p className="flex items-center">
                <InformationCircleIcon className="w-5 h-5 mr-2" />
                {t('fetchingVideoInfo')}
              </p>
            </div>
          )}

          {/* Error State */}
          {isSubmitted && isValidUrl === true && apiError && (
            <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
              <p className="flex items-center">
                <InformationCircleIcon className="w-5 h-5 mr-2" />
                Error: {getErrorMessage(apiError)}
              </p>
            </div>
          )}

          {/* Invalid URL Error */}
          {isSubmitted && isValidUrl === false && (
            <ErrorBoxSection message={t('invalidUrl')} />
          )}

          {/* Download Status */}
          {downloadingFormat && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg">
              <p className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Initiating download for {downloadingFormat.quality} quality...
              </p>
            </div>
          )}

          {/* Features */}
          {features.length > 0 && (
            <div className={`mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground select-none transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
              {features.map((feature, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs"
                >
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  {feature}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DownloaderSection;
